"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Menu } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { env } from "next-runtime-env";

interface NavItem {
  to: string;
  label: string;
  external?: boolean;
}

interface NavbarProps {
  items: NavItem[];
}

export default function Navbar({ items }: NavbarProps) {
  const DASHBOARD_URL =
    env("NEXT_PUBLIC_DASHBOARD_URL") || "https://cloud.getmac.io";
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-xl">
      <nav className="container px-4 mx-auto" aria-label="Main Navigation">
        <div className="max-w-6xl mx-auto">
          <div className="flex h-16 items-center justify-between">
            {/* Logo & Desktop Navigation */}
            <div className="flex items-center">
              {/* Logo */}
              <Link href="/" className="text-xl font-bold">
                <Image
                  src="/logos/getmac_horizontal_white.svg"
                  width={120}
                  height={30}
                  alt="GetMac Logo"
                  className="hidden dark:block"
                  priority
                />
                <Image
                  src="/logos/getmac_horizontal_dark.svg"
                  width={120}
                  height={30}
                  alt="GetMac Logo"
                  className="block dark:hidden"
                  priority
                />
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-8 ml-12">
                {items.map((item) => {
                  const { to, label, external = false } = item;
                  return (
                    <Link
                      key={label}
                      href={to}
                      className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                      {...(external
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                    >
                      {label}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* CTA Button, Theme Toggle & Mobile Menu Toggle */}
            <div className="flex items-center gap-4">
              <Link href={DASHBOARD_URL} className="hidden md:inline-flex">
                <Button className="text-sm bg-primary hover:bg-primary/90 text-white cursor-pointer rounded-full px-8">
                  Go to Dashboard
                </Button>
              </Link>

              <ThemeToggle />

              {/* Mobile Menu (Hamburger) */}
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    aria-label="Toggle menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="top" className="pt-8">
                  <div className="flex flex-col space-y-4 max-w-md mx-auto w-full">
                    {/* Logo in Mobile Menu */}
                    <SheetClose asChild>
                      <div className="flex justify-start mb-4">
                        <Link href="/" className="text-xl font-bold">
                          <Image
                            src="/logos/getmac_horizontal_white.svg"
                            width={120}
                            height={30}
                            alt="GetMac Logo"
                            className="hidden dark:block"
                            priority
                          />
                          <Image
                            src="/logos/getmac_horizontal_dark.svg"
                            width={120}
                            height={30}
                            alt="GetMac Logo"
                            className="block dark:hidden"
                            priority
                          />
                        </Link>
                      </div>
                    </SheetClose>

                    {/* Mobile Navigation */}
                    <nav
                      className="flex flex-col space-y-2 mt-6"
                      aria-label="Mobile Navigation"
                    >
                      {items.map((item) => {
                        const { to, label, external = false } = item;
                        const linkElement = (
                          <Link
                            href={to}
                            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-4 py-3 block w-full text-base rounded-lg hover:bg-muted"
                            {...(external
                              ? { target: "_blank", rel: "noopener noreferrer" }
                              : {})}
                          >
                            {label}
                          </Link>
                        );

                        return (
                          <div key={label} className="w-full">
                            <SheetClose asChild>{linkElement}</SheetClose>
                          </div>
                        );
                      })}
                    </nav>

                    {/* Mobile CTA Button */}
                    <div className="mt-6 pt-6 border-t border-border">
                      <SheetClose asChild>
                        <Link
                          href={DASHBOARD_URL}
                          className="w-full"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button className="text-sm bg-primary hover:bg-primary/90 text-white cursor-pointer w-full">
                            Go to Dashboard
                          </Button>
                        </Link>
                      </SheetClose>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
